/** Risky covered-call PoP range (min / max percent). */

export const CALLS_RISKY_POP_RANGE_MIN = 75;
export const CALLS_RISKY_POP_RANGE_MAX = 100;
export const CALLS_RISKY_POP_RANGE_STEP = 1;

export type CallsRiskyPopRange = { min: number; max: number };

export function defaultCallsRiskyPopRange(): CallsRiskyPopRange {
  return { min: CALLS_RISKY_POP_RANGE_MIN, max: CALLS_RISKY_POP_RANGE_MAX };
}

export function isFullRiskyPopRange(range: CallsRiskyPopRange): boolean {
  return range.min <= CALLS_RISKY_POP_RANGE_MIN && range.max >= CALLS_RISKY_POP_RANGE_MAX;
}

function clampPopPct(value: number): number {
  if (!Number.isFinite(value)) return CALLS_RISKY_POP_RANGE_MIN;
  return Math.min(
    CALLS_RISKY_POP_RANGE_MAX,
    Math.max(CALLS_RISKY_POP_RANGE_MIN, Math.round(value)),
  );
}

export function clampCallsRiskyPopRange(min: number, max: number): CallsRiskyPopRange {
  let lo = clampPopPct(min);
  let hi = clampPopPct(max);
  if (lo > hi) [lo, hi] = [hi, lo];
  return { min: lo, max: hi };
}

/** Accept `{ min, max }`, a legacy band array, or empty → full 75–100. */
export function parseCallsRiskyPopRange(value: unknown): CallsRiskyPopRange {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const rec = value as { min?: unknown; max?: unknown };
    if (rec.min != null || rec.max != null) {
      return clampCallsRiskyPopRange(Number(rec.min), Number(rec.max));
    }
  }
  if (Array.isArray(value)) {
    const pcts = value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));
    if (!pcts.length) return defaultCallsRiskyPopRange();
    const lo = Math.min(...pcts);
    const hi = Math.max(...pcts);
    // Legacy 95 band meant 95+; treat that as 100.
    return clampCallsRiskyPopRange(lo, hi >= 95 ? CALLS_RISKY_POP_RANGE_MAX : hi);
  }
  return defaultCallsRiskyPopRange();
}

export function popValueAsPercent(pop: number | null | undefined): number | null {
  const value = Number(pop);
  if (!Number.isFinite(value)) return null;
  return Math.abs(value) <= 1 ? value * 100 : value;
}

export function meetsRiskyPopRange(
  pop: number | null | undefined,
  range: CallsRiskyPopRange,
): boolean {
  if (isFullRiskyPopRange(range)) return true;
  const pct = popValueAsPercent(pop);
  if (pct == null) return false;
  return pct >= range.min && pct <= range.max;
}

export function formatCallsRiskyPopRange(range: CallsRiskyPopRange): string {
  if (isFullRiskyPopRange(range)) return "75–100%";
  return `${range.min}–${range.max}%`;
}

function rangeFillStyle(range: CallsRiskyPopRange): { left: string; width: string } {
  const span = CALLS_RISKY_POP_RANGE_MAX - CALLS_RISKY_POP_RANGE_MIN;
  const left = ((range.min - CALLS_RISKY_POP_RANGE_MIN) / span) * 100;
  const width = ((range.max - range.min) / span) * 100;
  return { left: `${left}%`, width: `${Math.max(width, 0)}%` };
}

export function renderCallsRiskyPopRangeSlider(
  range: CallsRiskyPopRange,
  opts: { id?: string; extraClass?: string } = {},
): string {
  const id = opts.id ?? "sfe-pop-range";
  const fill = rangeFillStyle(range);
  const extra = opts.extraClass ? ` ${opts.extraClass}` : "";
  return `<div class="sfe-pop-range${extra}" data-sfe-pop-range>
    <div class="sfe-pop-range-readout" data-sfe-pop-range-label>${formatCallsRiskyPopRange(range)}</div>
    <div class="sfe-pop-range-track">
      <i class="sfe-pop-range-fill" data-sfe-pop-range-fill style="left:${fill.left};width:${fill.width}"></i>
      <input type="range" id="${id}-min" min="${CALLS_RISKY_POP_RANGE_MIN}" max="${CALLS_RISKY_POP_RANGE_MAX}" step="${CALLS_RISKY_POP_RANGE_STEP}" value="${range.min}" data-sfe-pop-range="min" aria-label="Minimum PoP percent" />
      <input type="range" id="${id}-max" min="${CALLS_RISKY_POP_RANGE_MIN}" max="${CALLS_RISKY_POP_RANGE_MAX}" step="${CALLS_RISKY_POP_RANGE_STEP}" value="${range.max}" data-sfe-pop-range="max" aria-label="Maximum PoP percent" />
    </div>
  </div>`;
}

function paintCallsRiskyPopRangeSlider(root: HTMLElement, range: CallsRiskyPopRange): void {
  const minInput = root.querySelector<HTMLInputElement>('[data-sfe-pop-range="min"]');
  const maxInput = root.querySelector<HTMLInputElement>('[data-sfe-pop-range="max"]');
  const label = root.querySelector<HTMLElement>("[data-sfe-pop-range-label]");
  const fill = root.querySelector<HTMLElement>("[data-sfe-pop-range-fill]");
  if (minInput && document.activeElement !== minInput) minInput.value = String(range.min);
  if (maxInput && document.activeElement !== maxInput) maxInput.value = String(range.max);
  if (label) label.textContent = formatCallsRiskyPopRange(range);
  if (fill) {
    const style = rangeFillStyle(range);
    fill.style.left = style.left;
    fill.style.width = style.width;
  }
}

export function bindCallsRiskyPopRangeSlider(
  scope: ParentNode,
  onChange: (range: CallsRiskyPopRange, phase: "input" | "change") => void,
): void {
  const root = scope.querySelector<HTMLElement>("[data-sfe-pop-range]");
  if (!root || root.dataset.bound === "1") return;
  root.dataset.bound = "1";
  const minInput = root.querySelector<HTMLInputElement>('[data-sfe-pop-range="min"]');
  const maxInput = root.querySelector<HTMLInputElement>('[data-sfe-pop-range="max"]');
  if (!minInput || !maxInput) return;

  const readRange = (which: "min" | "max"): CallsRiskyPopRange => {
    let min = Number(minInput.value);
    let max = Number(maxInput.value);
    if (which === "min" && min > max) min = max;
    if (which === "max" && max < min) max = min;
    return clampCallsRiskyPopRange(min, max);
  };

  const emit = (which: "min" | "max", phase: "input" | "change"): void => {
    const range = readRange(which);
    minInput.value = String(range.min);
    maxInput.value = String(range.max);
    minInput.style.zIndex = which === "min" ? "2" : "1";
    maxInput.style.zIndex = which === "max" ? "2" : "1";
    paintCallsRiskyPopRangeSlider(root, range);
    onChange(range, phase);
  };

  minInput.addEventListener("input", () => emit("min", "input"));
  maxInput.addEventListener("input", () => emit("max", "input"));
  minInput.addEventListener("change", () => emit("min", "change"));
  maxInput.addEventListener("change", () => emit("max", "change"));
}

export type RiskyCoveredShareSource = {
  sfe_shares_held?: number | null;
  sfe_shares_available?: number | null;
  ready_call_contracts?: number | null;
  available_call_contracts?: number | null;
  opened_calls?: number | null;
};

/** Full 100-share lots if every held share is covered. Falls back to available, then 100. */
export function riskyCoveredShares(rec: RiskyCoveredShareSource): number {
  const held = Number(rec.sfe_shares_held);
  if (Number.isFinite(held) && held >= 100) return Math.floor(held / 100) * 100;
  const avail = Number(rec.sfe_shares_available);
  if (Number.isFinite(avail) && avail >= 100) return Math.floor(avail / 100) * 100;
  const ready = Number(rec.ready_call_contracts);
  if (Number.isFinite(ready) && ready > 0) return Math.floor(ready) * 100;
  const cap = Number(rec.available_call_contracts) || 0;
  const open = Number(rec.opened_calls) || 0;
  const contracts = Math.max(0, cap - open);
  if (contracts > 0) return Math.floor(contracts) * 100;
  return 100;
}

/** Locked-in share loss if assigned with `shares` covered: (avg − strike) × shares. */
export function callAssignmentMaxLoss(
  avgCost: number | null | undefined,
  strike: number | null | undefined,
  shares = 100,
): number | null {
  const avg = Number(avgCost);
  const k = Number(strike);
  const qty = Number(shares);
  if (!(avg > 0) || !(k > 0) || !(qty > 0)) return null;
  const perShare = avg - k;
  if (!(perShare > 0)) return 0;
  return perShare * qty;
}

export function callCoveredPremiumProfit(
  rec: { last_price?: number | null; mid?: number | null },
  shares = 100,
): number | null {
  const last = Number(rec.last_price);
  const mid = Number(rec.mid);
  const px = last > 0 ? last : mid > 0 ? mid : NaN;
  const qty = Number(shares);
  if (!(px > 0) || !(qty > 0)) return null;
  return px * qty;
}

export type TickerEarningsSource = {
  underlying_ticker?: string;
  next_earnings_date?: string | null;
  expiration_date?: string;
  expires_after_earnings?: boolean;
};

export function tickerEarningsDate(rec: TickerEarningsSource): string | null {
  const raw = rec.next_earnings_date;
  if (typeof raw !== "string") return null;
  const iso = raw.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return iso ? iso[1] : null;
}

export function buildTickerEarningsMap(sources: TickerEarningsSource[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const rec of sources) {
    const ticker = String(rec.underlying_ticker || "")
      .trim()
      .toUpperCase();
    const date = tickerEarningsDate(rec);
    if (ticker && date && !map.has(ticker)) map.set(ticker, date);
  }
  return map;
}

/** Copy a same-ticker earnings date onto rows that Market Data left blank. */
export function applyTickerEarnings<T extends TickerEarningsSource>(
  rows: T[],
  sources: TickerEarningsSource[] = [],
): T[] {
  const map = buildTickerEarningsMap([...sources, ...rows]);
  return rows.map((rec) => {
    const ticker = String(rec.underlying_ticker || "")
      .trim()
      .toUpperCase();
    const date = tickerEarningsDate(rec) ?? map.get(ticker) ?? null;
    if (!date) return rec;
    const exp = rec.expiration_date?.trim().slice(0, 10);
    return {
      ...rec,
      next_earnings_date: date,
      expires_after_earnings:
        rec.expires_after_earnings ?? (exp ? date <= exp : rec.expires_after_earnings),
    };
  });
}
